import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Users, Package, ShoppingBag, AlertTriangle, DollarSign, CheckCircle, XCircle, Eye, Loader2, TrendingUp, FileCheck } from 'lucide-react';
import { toast } from 'sonner';

function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [pendingListings, setPendingListings] = useState([]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [resolution, setResolution] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(function() {
    fetchData();
  }, []);

  const fetchData = async function() {
    try {
      const results = await Promise.all([
        adminAPI.getAnalytics(),
        adminAPI.getPendingListings(),
        adminAPI.getUsers(),
        adminAPI.getAllOrders(),
        adminAPI.getDisputes(),
      ]);
      setAnalytics(results[0].data);
      setPendingListings(results[1].data);
      setUsers(results[2].data);
      setOrders(results[3].data);
      setDisputes(results[4].data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveListing = async function(listingId) {
    setProcessing(true);
    try {
      await adminAPI.approveListing(listingId);
      toast.success('Listing approved');
      fetchData();
      setSelectedListing(null);
    } catch (error) {
      toast.error('Failed to approve listing');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectListing = async function(listingId) {
    if (!rejectReason) {
      toast.error('Please provide a reason');
      return;
    }
    setProcessing(true);
    try {
      await adminAPI.rejectListing(listingId, rejectReason);
      toast.success('Listing rejected');
      fetchData();
      setSelectedListing(null);
      setRejectReason('');
    } catch (error) {
      toast.error('Failed to reject listing');
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifyUser = async function(userId) {
    try {
      await adminAPI.verifyUser(userId);
      toast.success('User verified');
      fetchData();
    } catch (error) {
      toast.error('Failed to verify user');
    }
  };

  const handleResolveDispute = async function() {
    if (!resolution) {
      toast.error('Please provide a resolution');
      return;
    }
    setProcessing(true);
    try {
      await adminAPI.resolveDispute(selectedDispute.id, resolution);
      toast.success('Dispute resolved');
      fetchData();
      setSelectedDispute(null);
      setResolution('');
    } catch (error) {
      toast.error('Failed to resolve dispute');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = function(status) {
    const colors = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-sky-100 text-sky-800',
      in_fulfillment: 'bg-blue-100 text-blue-800',
      delivered: 'bg-emerald-100 text-emerald-800',
      completed: 'bg-green-100 text-green-800',
      disputed: 'bg-red-100 text-red-800',
      open: 'bg-red-100 text-red-800',
      resolved: 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <Layout showFooter={false}>
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
          </div>
        </div>
      </Layout>
    );
  }

  const hasPending = (analytics?.pending_sellers || 0) > 0 || (analytics?.pending_listings || 0) > 0 || (analytics?.open_disputes || 0) > 0;

  return (
    <Layout showFooter={false}>
      <div className="container mx-auto px-4 lg:px-8 py-8" data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Admin Dashboard</h1>
          <p className="text-slate-600">Manage the marketplace platform</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <div className="stat-card-value">{analytics?.total_users || 0}</div>
                <div className="stat-card-label">Users</div>
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="stat-card-value">{analytics?.total_listings || 0}</div>
                <div className="stat-card-label">Listings</div>
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="stat-card-value">{analytics?.total_orders || 0}</div>
                <div className="stat-card-label">Orders</div>
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="stat-card-value">{analytics?.total_leads || 0}</div>
                <div className="stat-card-label">Leads</div>
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="stat-card-value">${(analytics?.total_revenue || 0).toLocaleString()}</div>
                <div className="stat-card-label">Revenue</div>
              </div>
            </div>
          </Card>
        </div>

        {hasPending && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-6">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <div className="flex gap-6 text-sm">
                  {(analytics?.pending_sellers || 0) > 0 && (
                    <span className="text-amber-800">{analytics.pending_sellers} sellers pending verification</span>
                  )}
                  {(analytics?.pending_listings || 0) > 0 && (
                    <span className="text-amber-800">{analytics.pending_listings} listings pending approval</span>
                  )}
                  {(analytics?.open_disputes || 0) > 0 && (
                    <span className="text-amber-800">{analytics.open_disputes} open disputes</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="listings">
          <TabsList>
            <TabsTrigger value="listings">Pending Listings ({pendingListings.length})</TabsTrigger>
            <TabsTrigger value="users">Users ({users.length})</TabsTrigger>
            <TabsTrigger value="orders">Orders ({orders.length})</TabsTrigger>
            <TabsTrigger value="disputes">Disputes ({disputes.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="listings" className="mt-4">
            <Card>
              <CardContent className="p-0">
                {pendingListings.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">No pending listings</div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Seller</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingListings.map(function(listing) {
                          return (
                            <TableRow key={listing.id}>
                              <TableCell className="font-medium">{listing.title}</TableCell>
                              <TableCell>{listing.seller_name}</TableCell>
                              <TableCell>{listing.category}</TableCell>
                              <TableCell>{new Date(listing.created_at).toLocaleDateString()}</TableCell>
                              <TableCell>
                                <Button size="sm" variant="outline" onClick={function() { setSelectedListing(listing); }}>
                                  <Eye className="w-4 h-4 mr-1" />
                                  Review
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="mt-4">
            <Card>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map(function(user) {
                        return (
                          <TableRow key={user.id}>
                            <TableCell className="font-medium">{user.full_name}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">{user.role}</Badge>
                            </TableCell>
                            <TableCell>
                              {user.is_verified ? (
                                <Badge className="bg-emerald-100 text-emerald-800">Verified</Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-800">Pending</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {!user.is_verified && user.role === 'seller' && (
                                <Button size="sm" onClick={function() { handleVerifyUser(user.id); }} data-testid={'verify-user-' + user.id}>
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Verify
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="orders" className="mt-4">
            <Card>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Campaign</TableHead>
                        <TableHead>Buyer</TableHead>
                        <TableHead>Seller</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map(function(order) {
                        return (
                          <TableRow key={order.id}>
                            <TableCell className="font-medium">{order.campaign_name}</TableCell>
                            <TableCell>{order.buyer_name}</TableCell>
                            <TableCell>{order.seller_name}</TableCell>
                            <TableCell>
                              <Badge className={getStatusColor(order.status)}>
                                {order.status.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell>${order.total_price.toLocaleString()}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="disputes" className="mt-4">
            <Card>
              <CardContent className="p-0">
                {disputes.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">No disputes</div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Order</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {disputes.map(function(dispute) {
                          return (
                            <TableRow key={dispute.id}>
                              <TableCell className="font-mono text-sm">{dispute.order_id.slice(0, 8)}...</TableCell>
                              <TableCell>{dispute.reason}</TableCell>
                              <TableCell>
                                <Badge className={getStatusColor(dispute.status)}>{dispute.status}</Badge>
                              </TableCell>
                              <TableCell>{new Date(dispute.created_at).toLocaleDateString()}</TableCell>
                              <TableCell>
                                {dispute.status === 'open' && (
                                  <Button size="sm" onClick={function() { setSelectedDispute(dispute); }}>
                                    Resolve
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={!!selectedListing} onOpenChange={function() { setSelectedListing(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Review Listing</DialogTitle>
              <DialogDescription>Review and approve or reject this listing</DialogDescription>
            </DialogHeader>
            {selectedListing && (
              <div className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-slate-500">Title</div>
                    <div className="font-medium">{selectedListing.title}</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Seller</div>
                    <div className="font-medium">{selectedListing.seller_name}</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Category</div>
                    <div>{selectedListing.category}</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Regions</div>
                    <div className="flex flex-wrap gap-1">
                      {(selectedListing.regions || []).map(function(r) {
                        return <Badge key={r} variant="outline" className="text-xs">{r}</Badge>;
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Description</div>
                  <p className="text-sm text-slate-700">{selectedListing.description}</p>
                </div>
                
                <div className="space-y-2">
                  <div className="text-sm text-slate-500">Rejection Reason (if rejecting)</div>
                  <Textarea
                    placeholder="Provide a reason for rejection..."
                    value={rejectReason}
                    onChange={function(e) { setRejectReason(e.target.value); }}
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                    onClick={function() { handleRejectListing(selectedListing.id); }}
                    disabled={processing}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={function() { handleApproveListing(selectedListing.id); }}
                    disabled={processing}
                    data-testid="approve-listing-btn"
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Approve
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!selectedDispute} onOpenChange={function() { setSelectedDispute(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Resolve Dispute</DialogTitle>
              <DialogDescription>Provide a resolution for this dispute</DialogDescription>
            </DialogHeader>
            {selectedDispute && (
              <div className="space-y-4 pt-4">
                <div>
                  <div className="text-sm text-slate-500">Reason</div>
                  <div className="font-medium">{selectedDispute.reason}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500">Description</div>
                  <p className="text-sm text-slate-700">{selectedDispute.description}</p>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-slate-500">Resolution</div>
                  <Textarea
                    placeholder="Describe the resolution..."
                    value={resolution}
                    onChange={function(e) { setResolution(e.target.value); }}
                    rows={4}
                  />
                </div>
                <Button onClick={handleResolveDispute} disabled={processing} className="w-full">
                  {processing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileCheck className="w-4 h-4 mr-2" />
                  )}
                  Resolve Dispute
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default AdminDashboard;
